import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import { FiGlobe, FiMenu, FiX, FiExternalLink } from 'react-icons/fi';

const Navbar = () => {
  const { t, toggleLanguage, language } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('nav.home'), href: '#home' },
    { name: t('nav.services'), href: '#services' },
    { name: t('nav.about'), href: '#about' },
    { name: t('nav.contact'), href: '#contact' },
  ];

  const portalUrl = "https://albertorodriguez.mytaxportal.online/";

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-3' : 'bg-transparent py-5'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <a href="#home" className={`text-2xl font-bold tracking-tighter ${isScrolled ? 'text-blue-900' : 'text-white'}`}>
              Inmi<span className="text-blue-500">Taxes</span>
            </a>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <a key={link.name} href={link.href} className={`font-medium hover:text-blue-500 transition-colors ${isScrolled ? 'text-gray-700' : 'text-gray-100'}`}>
                {link.name}
              </a>
            ))}
            
            <a 
              href={portalUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-bold transition-all transform hover:scale-105 ${
                isScrolled 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-white text-blue-900 hover:bg-blue-50'
              }`}
            >
              <span>{t('nav.portal')}</span>
              <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
            </a>

            <button 
              onClick={toggleLanguage} 
              className={`flex items-center space-x-2 px-3 py-2 rounded-full border transition-colors ${isScrolled ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'border-white/30 text-white hover:bg-white/10'}`}
            >
              <SafeIcon icon={FiGlobe} className="w-4 h-4" />
              <span className="text-sm">{t('nav.switchLang')}</span>
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-4">
             <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className={`${isScrolled ? 'text-gray-900' : 'text-white'}`}>
              <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white shadow-xl absolute top-full left-0 w-full animate-in fade-in slide-in-from-top-4 duration-200">
          <div className="px-4 pt-2 pb-6 space-y-2">
            {navLinks.map((link) => (
              <a 
                key={link.name} 
                href={link.href} 
                className="block px-3 py-3 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <div className="pt-4 border-t border-gray-100 space-y-3">
              <a 
                href={portalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-bold"
              >
                <span>{t('nav.portal')}</span>
                <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
              </a>
              <button 
                onClick={() => { toggleLanguage(); setIsMobileMenuOpen(false); }} 
                className="flex items-center justify-center space-x-2 w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium"
              >
                <SafeIcon icon={FiGlobe} className="w-5 h-5" />
                <span>{t('nav.switchLang')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;